import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { type MouseEvent, useMemo, useState } from 'react';

import type { SearchableSelectOption } from '@/components/SearchableSelect';
import { PAGE_SIZES } from '@/constants/common';
import SearchBar, {
  addZeroCountOptions,
  getSearchableSelectOptions
} from '@/features/SearchBar';
import { REQUESTS_QUICK_FILTERS } from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.constants';
import {
  currentRequestAtom,
  filteredRequestsAtom,
  requestDetailsExpandedAtom,
  requestsFiltersAtom,
  requestsTablePageAtom,
  requestsTableRowsPerPageAtom,
  sensorsListAtom
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.store';
import type {
  Request,
  RequestStatusColorType
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.types';
import getStatusColor from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.utils';
import type { Order } from '@/types/common';
import getDateTime from '@/utils/getDateTime.utils';

import useStyles from './PcapRequestsTable.styles';

const statusFormatter = (status: RequestStatusColorType) => (
  <Chip variant="outlined" label={status} color={getStatusColor(status)} />
);

const PcapRequestsTable = () => {
  const [filteredRequests] = useAtom(filteredRequestsAtom);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('start');
  const [page, setPage] = useAtom(requestsTablePageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(requestsTableRowsPerPageAtom);
  const [requestDetailsExpanded, setRequestDetailsExpanded] = useAtom(
    requestDetailsExpandedAtom
  );
  const { classes } = useStyles();
  const [currentRequest, setCurrentRequest] = useAtom(currentRequestAtom);
  const [searchFilters, setSearchFilters] = useAtom(requestsFiltersAtom);
  const [sensors] = useAtom(sensorsListAtom);

  const COLUMNS = [
    {
      id: 'status',
      label: 'Status',
      cell: (row: Request) => statusFormatter(row.status),
      width: 100
    },
    {
      id: 'requester',
      label: 'Requester',
      cell: (row: Request) => row.requester.name,
      width: 100
    },
    {
      id: 'start',
      label: 'Start',
      cell: (row: Request) => getDateTime(row.details.start, true),
      width: 100
    },
    {
      id: 'end',
      label: 'End',
      cell: (row: Request) => getDateTime(row.details.end, true),
      width: 100
    },
    {
      id: 'sensors',
      label: 'Sensors',
      cell: (row: Request) => row.details.sensors.join(', '),
      width: 100
    }
  ];

  const handleChangePage = (
    _event: React.MouseEvent | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPage(page);
    setRowsPerPage(Number(event.target.value));
  };

  const handleSort = (property: string) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMouseClick = (
    _e: MouseEvent<HTMLTableRowElement, globalThis.MouseEvent>,
    id: number
  ) => {
    if (currentRequest?.id === id && requestDetailsExpanded) {
      setRequestDetailsExpanded(false);
      return;
    }
    const foundRequest = filteredRequests.find((request) => request.id === id);
    if (foundRequest) {
      setCurrentRequest(foundRequest);
      if (!requestDetailsExpanded) {
        setRequestDetailsExpanded(true);
      }
    }
  };

  const requestsQuickFilters = useMemo(() => {
    return REQUESTS_QUICK_FILTERS.map((filter) => {
      let filterOptions: SearchableSelectOption[] = [];
      if (filter.field === 'sensors') {
        filterOptions = sensors.map((sensor) => ({
          ...sensor,
          count: filteredRequests.filter((request) =>
            JSON.stringify(request.details.sensors).includes(
              sensor.value.toString()
            )
          ).length
        }));
      } else {
        filterOptions = getSearchableSelectOptions(
          filteredRequests,
          filter.field,
          filter.title
        );
      }

      // If there are any search filters applied, add them in even if they are not in the filter options
      if (searchFilters?.[filter.field]) {
        const updatedFilters = addZeroCountOptions(
          filter,
          searchFilters,
          filterOptions
        );
        filterOptions = updatedFilters;
      }

      return {
        ...filter,
        config: {
          ...filter.config,
          variant: filter.config.variant
        },
        filterOptions: [...filter.filterOptions, ...filterOptions]
      };
    });
  }, [filteredRequests, sensors]);

  const visibleRows = useMemo(() => {
    let orderProperty = orderBy;
    if (filteredRequests[0] && orderBy in filteredRequests[0].details) {
      orderProperty = `details.${orderBy}`;
    }
    let result = sortBy(filteredRequests, orderProperty);
    if (order === 'desc') {
      result = result.reverse();
    }
    result = result.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return result;
  }, [order, orderBy, page, rowsPerPage, filteredRequests]);

  return (
    <Stack direction="column" className={classes.requestsTableWrapper}>
      <div className={classes.searchBar}>
        <SearchBar
          searchableSelectFilters={requestsQuickFilters}
          selectedFilters={searchFilters}
          onFilterChange={setSearchFilters}
        />
      </div>
      <TableContainer sx={{ height: '100%' }}>
        <Table className={classes.requestsTable} stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => (
                <TableCell
                  key={`requestHeader-${column.label}`}
                  className={classes.requestsTableHeaderCell}
                  style={{
                    width: column.width,
                    paddingLeft: index === 0 ? 16 : '',
                    borderBottom:
                      currentRequest?.id === visibleRows[0]?.id &&
                      requestDetailsExpanded
                        ? 'none'
                        : '1px solid var(--mui-palette-TableCell-border)'
                  }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow
                key={`requestRow-${row.id}`}
                sx={{ cursor: 'pointer' }}
                onClick={(e) => handleMouseClick(e, row.id)}
                className={
                  currentRequest?.id === row.id && requestDetailsExpanded
                    ? classes.selectedRow
                    : ''
                }
              >
                {COLUMNS.map((column, colIndex) => (
                  <TableCell
                    key={column.id}
                    style={{
                      width: column.width,
                      paddingLeft: colIndex === 0 ? 16 : ''
                    }}
                    className={
                      visibleRows[index + 1]?.id === currentRequest?.id &&
                      requestDetailsExpanded
                        ? classes.prevSelectedRowCell
                        : ''
                    }
                  >
                    {column.cell(row as Request)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {/* empty row to fill the table height */}
            <TableRow sx={{ height: '100%' }}>
              <TableCell colSpan={7} sx={{ padding: 0, border: 'none' }} />
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                style={{
                  borderTop: '1px solid var(--mui-palette-TableCell-border)'
                }}
                className={classes.requestsTablePagination}
                rowsPerPageOptions={PAGE_SIZES}
                count={visibleRows.length}
                rowsPerPage={rowsPerPage}
                page={rowsPerPage * page > visibleRows.length ? 0 : page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default PcapRequestsTable;
